/**
 * Smart auto-sail — the tap-to-sail QUARTERMASTER. Where the old autopilot only synthesised a straight-line
 * rudder (a course through an island pinned the hull on the shore forever, and the ship sailed straight past
 * the mark on arrival — the owner's report), this module owns the whole passage:
 *
 *   plan   — A* route around the SAME solid circles the local prediction collides with (packages/sim/nav.ts),
 *            string-pulled into a few straight sailor legs, each proven clear at (hull radius + margin);
 *   steer  — rudder toward the live waypoint, advancing by line-of-sight so corners round off naturally;
 *   trim   — full sail on passage, shortened inside AUTOSAIL.SLOW_R so the hull arrives with bare steerage;
 *   watch  — re-plans when wind/drift compromises the current leg, when the hull teleports (respawn / port
 *            exit), or when it sits pinned (beached) for AUTOSAIL.STUCK_S; gives up honestly after
 *            STUCK_GIVEUP_S so the captain is never lied to;
 *   arrive — inside AUTOSAIL.ARRIVE_R the caller furls the sails and drops the anchor (a real stop).
 *
 * CLIENT-ONLY INPUT SYNTHESIS: everything here flows through the same C_INPUT (rudder/sailTarget/buttons)
 * a thumb produces — zero wire changes, the server stays fully authoritative, the deterministic sim is
 * untouched. `hud.waypoint` remains the single source of truth for the destination; main.ts feeds it in and
 * applies the returned overrides. UI NOTE: the chart can draw the live route via the exposed `route` /
 * `destination` getters on the shared `autoSail` singleton (no drawing happens here).
 */
import { AUTOSAIL, WORLD_SIZE } from '@piratas/protocol';
import { buildNavGrid, planRoute, projectToWater, segmentClearFrom, type NavGrid, type NavPoint, type Obstacle } from '@piratas/sim';
import { autopilotRudder } from './navmath.js';

/** Per-input-tick command from the quartermaster. Exactly one of the terminal flags fires on its edge tick. */
export interface AutoSailCmd {
  rudder: number;
  sailTarget: number;
  /** Rising edge: a NEW destination was adopted this tick (caller weighs anchor + snapshots the sail trim). */
  engaged: boolean;
  /** Reached the mark this tick — caller furls sails, drops anchor and clears the chart course. */
  arrived: boolean;
  /** No navigable route to the mark (sealed off) — caller clears the course with an honest toast. */
  noRoute: boolean;
  /** Hull pinned beyond rescue (beached under sail for STUCK_GIVEUP_S) — caller clears with an honest toast. */
  aground: boolean;
}

export class AutoSail {
  private obstacles: readonly Obstacle[] = [];
  private shipR = 11;
  private size = WORLD_SIZE;
  private grid: NavGrid | null = null;      // built lazily per (obstacles, clearance); invalidated by setChart
  private dest: NavPoint | null = null;     // the raw hud.waypoint we are serving (identity for change detection)
  private target: NavPoint | null = null;   // dest projected into navigable water (tap ON an island → its shore)
  private wps: NavPoint[] = [];
  private wpIdx = 0;
  private replanCd = 0;
  private stuckS = 0;
  private stuckTotalS = 0;
  private lastX = 0; private lastY = 0;
  private hasLast = false;

  private get clearance(): number { return this.shipR + AUTOSAIL.CLEARANCE; }
  private get hardClearance(): number { return this.shipR + AUTOSAIL.HARD_CLEARANCE; }

  /** Update the solid-obstacle chart + hull radius (the SAME set fed to prediction.setObstacles). Cheap;
   *  invalidates the cached grid. An active course self-heals via live leg validation on the next ticks. */
  setChart(obstacles: readonly Obstacle[], shipR: number, size = WORLD_SIZE): void {
    this.obstacles = obstacles;
    this.shipR = shipR;
    this.size = size;
    this.grid = null;
  }

  /** True while a course is being sailed. */
  get active(): boolean { return this.dest !== null; }
  /** The mark actually navigated to (the tap projected into safe water) — for the chart UI. Null when idle. */
  get destination(): NavPoint | null { return this.target; }
  /** Remaining route (next turn point … final mark) — for the chart UI to draw. Empty when idle. */
  get route(): readonly NavPoint[] { return this.dest ? this.wps.slice(this.wpIdx) : []; }

  /** Drop any active course (manual override, chart cleared, arrival — the caller decides why). */
  clear(): void {
    this.dest = null;
    this.target = null;
    this.wps = [];
    this.wpIdx = 0;
    this.replanCd = 0;
    this.stuckS = 0;
    this.stuckTotalS = 0;
    this.hasLast = false;
  }

  /**
   * One quartermaster tick (call at INPUT_HZ with the PREDICTED local body). `dest` is hud.waypoint —
   * null clears, a changed point re-engages. Returns null when idle, otherwise the input overrides +
   * edge flags (see AutoSailCmd). Never touches the HUD or the wire itself.
   */
  update(dest: NavPoint | null, x: number, y: number, heading: number, speed: number, dt: number): AutoSailCmd | null {
    if (!dest) { if (this.dest) this.clear(); return null; }
    const cmd: AutoSailCmd = { rudder: 0, sailTarget: 0, engaged: false, arrived: false, noRoute: false, aground: false };
    const newDest = !this.dest || dest.x !== this.dest.x || dest.y !== this.dest.y;
    const jumped = this.hasLast && (x - this.lastX) ** 2 + (y - this.lastY) ** 2 > AUTOSAIL.TELEPORT_JUMP ** 2;
    this.lastX = x; this.lastY = y; this.hasLast = true;
    this.replanCd = Math.max(0, this.replanCd - dt);

    if (newDest) {
      this.dest = { x: dest.x, y: dest.y };
      this.stuckS = 0; this.stuckTotalS = 0;
      cmd.engaged = true;
      if (!this.plan(x, y)) { cmd.noRoute = true; this.clear(); return cmd; }
    } else if (jumped && !this.plan(x, y)) { // respawn / port-exit teleport → re-plan from the new seat
      cmd.noRoute = true; this.clear(); return cmd;
    }

    const t = this.target!;
    const dGoal = Math.hypot(t.x - x, t.y - y);
    if (dGoal <= AUTOSAIL.ARRIVE_R) { cmd.arrived = true; this.clear(); return cmd; }

    // advance by line-of-sight (corners round off as soon as the next leg is provably clear) …
    while (this.wpIdx < this.wps.length - 1 &&
      segmentClearFrom(x, y, this.wps[this.wpIdx + 1]!.x, this.wps[this.wpIdx + 1]!.y, this.obstacles, this.clearance)) this.wpIdx++;
    // … or by plain proximity (LOS to the next one may still be shadowed by the island we're rounding)
    let wp = this.wps[this.wpIdx]!;
    if (this.wpIdx < this.wps.length - 1 && Math.hypot(wp.x - x, wp.y - y) < AUTOSAIL.WP_REACH_R) wp = this.wps[++this.wpIdx]!;

    // live leg validation: wind/drift shoved us — re-plan when the CURRENT leg compromises the hard margin
    if (this.replanCd <= 0 && !segmentClearFrom(x, y, wp.x, wp.y, this.obstacles, this.hardClearance)) {
      this.replanCd = AUTOSAIL.REPLAN_COOLDOWN_S;
      if (!this.plan(x, y)) { cmd.noRoute = true; this.clear(); return cmd; }
      wp = this.wps[this.wpIdx]!;
    }

    // stuck watchdog: pinned with sails commanded up → re-plan; pinned beyond rescue → give up honestly
    if (speed < AUTOSAIL.STUCK_SPEED) { this.stuckS += dt; this.stuckTotalS += dt; }
    else { this.stuckS = 0; this.stuckTotalS = Math.max(0, this.stuckTotalS - dt); }
    if (this.stuckTotalS >= AUTOSAIL.STUCK_GIVEUP_S) { cmd.aground = true; this.clear(); return cmd; }
    if (this.stuckS >= AUTOSAIL.STUCK_S && this.replanCd <= 0) {
      this.stuckS = 0;
      this.replanCd = AUTOSAIL.REPLAN_COOLDOWN_S;
      if (!this.plan(x, y)) { cmd.noRoute = true; this.clear(); return cmd; }
      wp = this.wps[this.wpIdx]!;
    }

    cmd.rudder = autopilotRudder(x, y, heading, wp.x, wp.y);
    const lastLeg = this.wpIdx >= this.wps.length - 1;
    cmd.sailTarget = lastLeg ? Math.min(1, Math.max(AUTOSAIL.MIN_SAIL, dGoal / AUTOSAIL.SLOW_R)) : 1;
    return cmd;
  }

  /** (Re)compute the route from (x,y) to the water-projected destination. False = unreachable. */
  private plan(x: number, y: number): boolean {
    const d = this.dest!;
    if (!this.grid) this.grid = buildNavGrid(this.obstacles, this.clearance, this.size, AUTOSAIL.CELL);
    const t = projectToWater(d.x, d.y, this.obstacles, this.clearance + 2, this.size, AUTOSAIL.EDGE_INSET, x, y);
    const route = planRoute(x, y, t.x, t.y, this.grid, this.obstacles, this.clearance);
    if (!route) return false;
    this.target = t;
    this.wps = route;
    this.wpIdx = 0;
    return true;
  }
}

/** Shared instance: main.ts drives it each input tick; the chart UI reads `.route`/`.destination` to draw. */
export const autoSail = new AutoSail();
